import { InternalId } from '@edgebox/data-definition-kit';
import { ClientSiteEntity } from '@edgebox/sync-core-rest-client';
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { CSSProperties } from 'react';
import { ISyncCoreApiComponentState, SyncCoreApiComponent } from '../services/SyncCoreApiComponent';
import { ExternalLinkWithIcon } from './ExternalLinkWithIcon';
import { ExternalLink } from '@edgebox/react-components';
import { PLATFORM_GOVERNANCE_SITE_ICON } from './Icons';
import { isPlatformGovernanceSite, siteKindLabel, UNRESOLVED_SITE_LABEL } from './siteKind.utils';

interface IProps {
  id: InternalId;
  light?: boolean;
  className?: string;
  linkIconClassName?: string;
  style?: CSSProperties;
  noIcon?: boolean;
  link?: string;
  inline?: boolean;
  middle?: boolean;
  withTitle?: boolean;
}

interface IState extends ISyncCoreApiComponentState {
  site?: ClientSiteEntity;
  loaded?: boolean;
}

// A caller styles this component for the link it may render, underline included, and then leaves the
// className the same for the calls that pass no link at all. What is not a link does not borrow the
// look of one: every branch that renders a span drops the class the anchor owns.
const LINK_ONLY_CLASS_NAMES = ['text-decoration-underline'];

function withoutLinkStyling(className?: string): string {
  return (className || '')
    .split(' ')
    .filter((entry) => entry && !LINK_ONLY_CLASS_NAMES.includes(entry))
    .join(' ');
}

export class SiteName extends SyncCoreApiComponent<IProps, IState> {
  async load() {
    const { id } = this.props;

    // A site a reference names is not always a site this session can read: it may be gone, or it may
    // be one the engine keeps to itself. Either way the reference stays on the screen, so the answer
    // is what is known about it and never an endless wait.
    const site = await this.api.billing.sites.item(id).catch(() => undefined);

    return {
      site,
      loaded: true,
    };
  }

  render() {
    const { light, className, style, link, linkIconClassName, noIcon, inline, middle, withTitle } = this.props;

    const { site, loaded } = this.state;
    if (site) {
      // The platform's own source of terms is named by what it is, not by the name it carries
      // internally, and it is never offered as a place a reader can open: nobody set it up and
      // nobody administers it.
      if (isPlatformGovernanceSite(site)) {
        const kindLabel = siteKindLabel(site);

        if (inline) {
          return kindLabel;
        }

        return (
          <span
            className={`text-truncate d-inline-block ${withoutLinkStyling(className)} ${middle ? 'align-middle' : ''}`}
            style={style}
            title={withTitle ? kindLabel : undefined}
          >
            <FontAwesomeIcon icon={PLATFORM_GOVERNANCE_SITE_ICON} className="me-1" />
            {kindLabel}
          </span>
        );
      }

      if (link) {
        return noIcon ? (
          <ExternalLink to={link} className={className}>
            {site.name}
          </ExternalLink>
        ) : (
          <ExternalLinkWithIcon to={link} className={className} iconClassName={linkIconClassName}>
            {site.name}
          </ExternalLinkWithIcon>
        );
      }
      if (inline) {
        return site.name || null;
      }
      return (
        <span
          className={`text-truncate d-inline-block ${withoutLinkStyling(className)} ${middle ? 'align-middle' : ''}`}
          style={style}
          title={withTitle ? site.name : undefined}
        >
          {site.name}
        </span>
      );
    }

    if (loaded) {
      if (inline) {
        return UNRESOLVED_SITE_LABEL;
      }

      return (
        <span
          className={`text-muted text-truncate d-inline-block ${withoutLinkStyling(className)} ${middle ? 'align-middle' : ''}`}
          style={style}
          title={withTitle ? UNRESOLVED_SITE_LABEL : undefined}
        >
          <em>{UNRESOLVED_SITE_LABEL}</em>
        </span>
      );
    }

    return <FontAwesomeIcon icon={faSpinner} spin className={`text-${light ? 'light' : 'dark'}`} style={{ fontSize: '1.4em' }} />;
  }
}
